import { Flex, Line } from 'components';
import { FieldLabel, SwitchButton, TextField } from 'components/_form';
import {
  NestedSelect,
  IOption as OptionForNestedSelect
} from 'components/_form/NestedSelect/NestedSelect';
import { ColorPicker } from 'components/_form/ColorPicker/ColorPicker';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { Fragment, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IPriorityLevelFormFields } from 'types/priorityLevel';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { companySupportedLanguageCodes } from 'utilities/companyLanguageCodes';
import { PriorityLevelBadge } from '../../PriorityLevelBadge/PriorityLevelBadge';

const HeaderCell = styled.span`
  font-weight: 700;
`;

const LanguageLabel = styled.span`
  min-width: 88px;
  font-weight: 700;
  text-transform: lowercase;
`;

const LanguageGrid = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(72px, 0.35fr) minmax(120px, 1fr);
  gap: 8px 12px;
  align-items: center;
  width: 100%;
`;

function normLanguageCode(code: string): string {
  return code.trim().toLowerCase();
}

function priorityLevelLanguageCodes(
  company: Parameters<typeof companySupportedLanguageCodes>[0],
  extraCodes: string[] = []
) {
  const supported = companySupportedLanguageCodes(company);
  const defaultCode = (company?.default_language_code ?? supported[0] ?? 'pl').trim();
  const ordered = [defaultCode, ...supported, ...extraCodes];
  const seen = new Set<string>();

  return ordered.filter((code) => {
    const norm = normLanguageCode(code);
    if (!norm || seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });
}

export const PriorityLevelForm: React.FC = () => {
  const { data: areasData = [] } = useAreasQuery();
  const { data: company } = useMainCompanyQuery();
  const areas = useMemo<OptionForNestedSelect[]>(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );

  const { t } = useTranslation();
  const languageCodes = priorityLevelLanguageCodes(company);
  const defaultLanguageCode = (company?.default_language_code ?? languageCodes[0] ?? 'pl')
    .trim()
    .toLowerCase();
  const defaultLanguage = languageCodes.find(
    (code) => code.trim().toLowerCase() === defaultLanguageCode
  );

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext<IPriorityLevelFormFields & { active?: boolean }>();

  const defaultNameTranslation = defaultLanguage
    ? watch(`name_translations.${defaultLanguage}` as const)
    : watch('name');
  const defaultCodeIdTranslation = defaultLanguage
    ? watch(`id_code_translations.${defaultLanguage}` as const)
    : watch('id_code');
  const colorCode = watch('color_code');

  const previewSource = {
    name: defaultNameTranslation,
    id_code: defaultCodeIdTranslation,
    color_code: colorCode
  };

  useEffect(() => {
    setValue('name', defaultNameTranslation ?? '', {
      shouldDirty: false,
      shouldValidate: true
    });
  }, [defaultNameTranslation, setValue]);

  useEffect(() => {
    setValue('id_code', defaultCodeIdTranslation ?? '', {
      shouldDirty: false,
      shouldValidate: true
    });
  }, [defaultCodeIdTranslation, setValue]);

  const languageLabel = (code: string) =>
    t(`common.${code}`, { defaultValue: code });

  return (
    <Flex flexDirection="column" gap="10px">
      <input type="hidden" {...register('name')} />
      <input type="hidden" {...register('id_code')} />

      <Flex alignItems="center" flexDirection={['column', 'row']}>
        <Flex width={['100%', '20%']}>
          <FieldLabel>{t('administrationTasksView.clickToChooseColor')}</FieldLabel>
        </Flex>
        <Flex
          width={['100%', '80%']}
          gap="16px"
          alignItems="center"
          flexWrap="wrap"
        >
          <Controller
            control={control}
            name="color_code"
            render={({ field: { value, onChange } }) => (
              <ColorPicker controlledDisplayColorPicker={value} onChange={onChange} />
            )}
          />
          <PriorityLevelBadge priorityLevel={previewSource} />
        </Flex>
      </Flex>
      {errors.color_code && (
        <Flex justifyContent="end" width="100%">
          <Flex width={['100%', '80%']}>
            <span>{t(String(errors.color_code.message))}</span>
          </Flex>
        </Flex>
      )}

      <Flex flexDirection={['column', 'row']} alignItems={['stretch', 'flex-start']}>
        <Flex width={['100%', '20%']} pt={1}>
          <FieldLabel>{t('administrationTasksView.languageVersions')}</FieldLabel>
        </Flex>
        <Flex width={['100%', '80%']} flexDirection="column" gap="8px">
          <LanguageGrid>
            <span />
            <HeaderCell>{t('administrationTasksView.code')}</HeaderCell>
            <HeaderCell>{t('administrationTasksView.name')}</HeaderCell>

            {languageCodes.map((locale) => {
              const isDefault = locale.trim().toLowerCase() === defaultLanguageCode;
              const codeField = `id_code_translations.${locale}` as const;
              const nameField = `name_translations.${locale}` as const;

              return (
                <Fragment key={locale}>
                  <LanguageLabel>{languageLabel(locale)}</LanguageLabel>
                  <TextField
                    {...register(codeField)}
                    error={t(
                      errors.id_code_translations?.[locale]?.message ||
                        (isDefault ? errors.id_code?.message : '') ||
                        ''
                    )}
                  />
                  <TextField
                    {...register(nameField)}
                    error={t(
                      errors.name_translations?.[locale]?.message ||
                        (isDefault ? errors.name?.message : '') ||
                        ''
                    )}
                  />
                </Fragment>
              );
            })}
          </LanguageGrid>
        </Flex>
      </Flex>

      <Line variant="secondary" />

      <Flex flexDirection={['column', 'row']}>
        <Flex width={['100%', '20%']} flexDirection="column" gap="4px">
          <FieldLabel>{t('administrationTasksView.active?')}</FieldLabel>
          <Controller
            control={control}
            name="active"
            render={({ field: { onChange, value } }) => (
              <SwitchButton
                value={!!value}
                onChange={(checked) => {
                  onChange(checked);
                }}
              />
            )}
          />
        </Flex>
        <Flex width={['100%', '80%']}>
          <Controller
            control={control}
            name="areas"
            render={({ field: { onChange, value } }) => (
              <NestedSelect
                width="100%"
                label={t('administrationTasksView.usedInAreas')}
                helperText="common.leave blank if all company"
                selectedOptions={value ?? []}
                setSelectedOptions={onChange}
                options={areas}
              />
            )}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

/** Buduje domyślne puste tłumaczenia dla formularza. */
export function emptyPriorityLevelTranslations(
  company: Parameters<typeof companySupportedLanguageCodes>[0]
) {
  const locales = priorityLevelLanguageCodes(company);
  return {
    name_translations: Object.fromEntries(locales.map((loc) => [loc, ''])),
    id_code_translations: Object.fromEntries(locales.map((loc) => [loc, '']))
  };
}

/** Wartości początkowe formularza edycji (w tym tłumaczenia). */
export function priorityLevelToFormDefaults(
  priorityLevel: {
    name: string;
    id_code: string;
    color_code: string;
    active: boolean;
    order: number;
    name_translations?: Record<string, string>;
    id_code_translations?: Record<string, string>;
  },
  company: Parameters<typeof companySupportedLanguageCodes>[0]
) {
  const locales = priorityLevelLanguageCodes(company);
  const defaultLocale = locales[0];

  return {
    name: priorityLevel.name,
    id_code: priorityLevel.id_code,
    color_code: priorityLevel.color_code,
    active: priorityLevel.active,
    order: priorityLevel.order,
    name_translations: Object.fromEntries(
      locales.map((loc) => [
        loc,
        priorityLevel.name_translations?.[loc] ??
          (loc === defaultLocale ? priorityLevel.name : '')
      ])
    ),
    id_code_translations: Object.fromEntries(
      locales.map((loc) => [
        loc,
        priorityLevel.id_code_translations?.[loc] ??
          (loc === defaultLocale ? priorityLevel.id_code : '')
      ])
    )
  };
}

/** Payload API bez pól formularza areas. */
export function buildPriorityLevelPayload(
  data: IPriorityLevelFormFields & { active?: boolean; areas?: OptionForNestedSelect[] },
  areaIdsForApi: string[],
  company?: Parameters<typeof companySupportedLanguageCodes>[0]
) {
  const { areas: _areas, ...rest } = data;
  const locales = priorityLevelLanguageCodes(company, [
    ...Object.keys(rest.name_translations ?? {}),
    ...Object.keys(rest.id_code_translations ?? {})
  ]);
  const defaultLocale = locales[0];
  const name_translations = Object.fromEntries(
    locales.map((loc) => [loc, (rest.name_translations?.[loc] ?? '').trim()])
  );
  const id_code_translations = Object.fromEntries(
    locales.map((loc) => [loc, (rest.id_code_translations?.[loc] ?? '').trim()])
  );
  const defaultName = defaultLocale ? name_translations[defaultLocale] : rest.name?.trim();
  const defaultCodeId = defaultLocale
    ? id_code_translations[defaultLocale]
    : rest.id_code?.trim();

  return {
    ...rest,
    name: defaultName,
    id_code: defaultCodeId,
    name_translations,
    id_code_translations,
    area_ids: areaIdsForApi
  };
}
